/**
 * FI (Finanzas) — shared formatting + tiny presentational helpers.
 * Self-contained so every FI component renders consistently (dark theme).
 */
export function formatCurrency(n: number | null | undefined, currency = 'USD'): string {
  if (n == null || Number.isNaN(n)) return '—'
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency, minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(n)
}

/** Compact form for axis ticks / big tiles: $31.2k, $1.2M */
export function formatCompact(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '—'
  const abs = Math.abs(n)
  if (abs >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`
  if (abs >= 1_000) return `$${(n / 1_000).toFixed(1)}k`
  return `$${Math.round(n)}`
}

export function formatPct(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '—'
  return `${n.toFixed(1)}%`
}

/** 3-tier semantic tone for a margin % (green ≥40, amber 20-39, red <20). */
export function marginTone(pct: number | null | undefined): { text: string; bg: string; dot: string } {
  if (pct == null) return { text: 'text-gray-400', bg: 'bg-gray-500/15', dot: 'bg-gray-500' }
  if (pct >= 40) return { text: 'text-green-400', bg: 'bg-green-500/15', dot: 'bg-green-500' }
  if (pct >= 20) return { text: 'text-yellow-400', bg: 'bg-yellow-500/15', dot: 'bg-yellow-500' }
  return { text: 'text-red-400', bg: 'bg-red-500/15', dot: 'bg-red-500' }
}

/** Stable color per known vendor for the cost-breakdown chart. */
export const VENDOR_COLORS: Record<string, string> = {
  CrowdStrike: '#ef4444',
  JumpCloud: '#22c55e',
  Cloudflare: '#f59e0b',
  SocRadar: '#8b5cf6',
  Drata: '#3b82f6',
  'Strike.sh': '#ec4899',
  Jarix: '#14b8a6',
}
export function vendorColor(vendor: string): string {
  return VENDOR_COLORS[vendor] || '#64748b'
}

/** Green up / red down delta chip. */
export function GrowthBadge({ pct }: { pct: number | null | undefined }) {
  if (pct == null || Number.isNaN(pct)) return <span className="text-gray-500 text-xs">—</span>
  const up = pct >= 0
  return (
    <span className={`text-xs font-medium ${up ? 'text-green-400' : 'text-red-400'}`}>
      {up ? '▲' : '▼'} {Math.abs(pct).toFixed(1)}%
    </span>
  )
}

/** Minimal inline sparkline (no deps) — normalizes values to a small SVG. */
export function Sparkline({ data, width = 80, height = 22, color = '#22d3ee' }: {
  data: number[]; width?: number; height?: number; color?: string
}) {
  if (!data || data.length < 2) return <span className="text-gray-600 text-xs">—</span>
  const min = Math.min(...data)
  const max = Math.max(...data)
  const span = max - min || 1
  const step = width / (data.length - 1)
  const pts = data.map((v, i) => `${(i * step).toFixed(1)},${(height - ((v - min) / span) * height).toFixed(1)}`).join(' ')
  return (
    <svg width={width} height={height} className="inline-block align-middle">
      <polyline points={pts} fill="none" stroke={color} strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  )
}
